import { FunctionComponent, useCallback } from "react";
import ReactQuill from "react-quill";
import clsx from "clsx";

import "react-quill/dist/quill.snow.css";

import Label from "../Label/Label";

const Link = ReactQuill.Quill.import("formats/link");
const PROTOCOL_WHITELIST = ["http", "https", "mailto", "tel", "radar", "rdar", "smb", "sms"];

class AbsoluteLinkFormat extends Link {
  static sanitize(url: string) {
    // Run default sanitize method from Quill
    const sanitizedUrl = super.sanitize(url);

    // Not whitelisted URL based on protocol so, let's return `blank`
    if (!sanitizedUrl || sanitizedUrl === "about:blank") return sanitizedUrl;

    // Verify if the URL already have a whitelisted protocol
    const hasWhitelistedProtocol = PROTOCOL_WHITELIST.some(function (protocol: string) {
      return sanitizedUrl.startsWith(protocol);
    });

    if (hasWhitelistedProtocol) return sanitizedUrl;

    // if not, then append only 'https' to not to be a relative URL
    return `https://${sanitizedUrl}`;
  }
}
ReactQuill.Quill.register(AbsoluteLinkFormat, true);

interface TextEditorProps {
  className?: string;
  error?: string;
  label?: string;
  modules?: { [key: string]: any };
  onChange: (value: string) => void;
  placeholder?: string;
  value: string;
}
const TextEditor: FunctionComponent<TextEditorProps> = ({
  className,
  error,
  label,
  modules,
  onChange,
  placeholder,
  value,
}) => {
  const handleChange = useCallback((value: string) => {
    const isQuillEmpty = value.replace(/<(.|\n)*?>/g, "").length === 0;
    if (isQuillEmpty) onChange("");
    else onChange(value);
  }, []);

  const toolbar = Array.isArray(modules?.toolbar)
    ? modules?.toolbar
    : [
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link"],
      ];

  const clipboard = modules?.clipboard ?? {
    // toggle to add extra line breaks when pasting HTML:
    matchVisual: false,
  };

  return (
    <div className={clsx(className, "tw-relative")}>
      {label && <Label className="tw-mb-2">{label}</Label>}
      <ReactQuill
        className={clsx(error && "ql-error")}
        theme="snow"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        modules={{ toolbar, clipboard }}
      />
      {error && <p className="tw-mt-1 tw-mb-0 tw-text-sm tw-italic tw-text-red-600">{error}</p>}
    </div>
  );
};

export default TextEditor;
